/**
 * CommentsPanel — threaded comments on a task.
 *
 * Props:
 *   sidebarMode  — when true, renders compact inside the task-detail sidebar
 *                  with icon-only ✎/🗑 edit/delete buttons.
 *
 * Rules:
 *   Anyone in the app can add a comment.
 *   Editing is restricted to the comment's author.
 *   Deletion: author or admin.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../../lib/api';
import { formatExactEntry, formatDateTime, memberDisplayName, initials } from '../../../lib/format';
import { renderMentionText } from '../MentionTextarea';
import { useSpacesUpload, validateFiles, ACCEPT_STRING } from '../../../lib/useSpacesUpload';
import { renderMixedContent } from '../../shared/AttachmentComponents';
import { Paperclip } from 'lucide-react';
import { QueryBadge } from '../review/ReviewQueries';
import { useMentionInContentEditable, extractTextWithMentions } from '../../../hooks/useMentionInContentEditable';

// Copying a posted comment puts its images on the clipboard only as <img> tags
// in text/html — text/plain holds just the alt text (the file name, e.g.
// "image (20).png"). Split the HTML into ordered text/image parts so paste can
// re-attach the real images. Returns null when there's no image to recover.
const PASTE_BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'TR', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE']);
function splitPastedHtml(html) {
  if (!html || !/<img/i.test(html)) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const parts = [];
  let text = '';
  const flush = () => { if (text) parts.push({ type: 'text', value: text }); text = ''; };
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) { text += node.textContent; return; }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (/^(SCRIPT|STYLE|BUTTON)$/.test(node.nodeName)) return;
    if (node.nodeName === 'BR') { text += '\n'; return; }
    if (node.nodeName === 'IMG') {
      const src = node.getAttribute('src');
      // Skip avatars — they're not part of the comment content
      if (!src || node.closest('[class*="avatar"]')) return;
      flush();
      parts.push({ type: 'img', src, name: node.getAttribute('alt') || 'image' });
      return;
    }
    const isBlock = PASTE_BLOCK_TAGS.has(node.nodeName);
    if (isBlock && text && !text.endsWith('\n')) text += '\n';
    node.childNodes.forEach(walk);
    if (isBlock && text && !text.endsWith('\n')) text += '\n';
  };
  walk(doc.body);
  flush();
  if (!parts.some(p => p.type === 'img')) return null;
  if (parts[0].type === 'text') parts[0].value = parts[0].value.replace(/^\s+/, '');
  const last = parts[parts.length - 1];
  if (last.type === 'text') last.value = last.value.replace(/\s+$/, '');
  return parts.filter(p => p.type === 'img' || p.value);
}

async function fetchImageAsFile(src, name) {
  // cache:'reload' — the <img> copy in the browser cache was fetched without
  // CORS headers, and reusing it would make this cross-origin fetch fail.
  const res = await fetch(src, { cache: 'reload' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  if (!blob.type.startsWith('image/')) throw new Error('Not an image');
  const fileName = /\.[a-z0-9]+$/i.test(name) ? name : `${name}.${blob.type.split('/')[1] || 'png'}`;
  return new File([blob], fileName, { type: blob.type });
}

export default function CommentsPanel({ appId, taskId, members, me, sidebarMode }) {
  const api = useApi();
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [posting, setPosting]     = useState(false);
  const [error, setError]         = useState(null);
  const [editingId, setEditId]    = useState(null);
  const [visibleCount, setVisibleCount] = useState(10);
  const composeRef                = useRef(null);
  const savedRange                = useRef(null);
  const fileInputRef              = useRef(null);
  // Edit-mode refs
  const editRef                   = useRef(null);
  const editFileInputRef          = useRef(null);
  const editSavedRange            = useRef(null);

  // @ mention for compose box
  const {
    handleMentionKeyDown: composeMentionKeyDown,
    handleMentionInput:   composeMentionInput,
    MentionDropdown:      ComposeMentionDropdown,
  } = useMentionInContentEditable(composeRef, members);

  // @ mention for edit box
  const {
    handleMentionKeyDown: editMentionKeyDown,
    handleMentionInput:   editMentionInput,
    MentionDropdown:      EditMentionDropdown,
  } = useMentionInContentEditable(editRef, members);
  const { uploadFiles, uploading } = useSpacesUpload(appId, 'task_comment');
  const [hasContent, setHasContent] = useState(false);
  const [fileError, setFileError]   = useState(null);
  const [editFileError, setEditFileError] = useState(null);

  const updateHasContent = () => {
    const el = composeRef.current;
    if (!el) { setHasContent(false); return; }
    const hasText = !!el.textContent?.trim();
    const hasImg  = !!el.querySelector('.cmt-inline-img-wrap');
    setHasContent(hasText || hasImg);
  };

  const SENTINEL_ID = 'cmt-caret-sentinel';

  const plantSentinel = (editorEl) => {
    // Remove any leftover sentinel first
    editorEl.querySelector(`#${SENTINEL_ID}`)?.remove();
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (editorEl.contains(r.commonAncestorContainer)) {
        const s = document.createElement('span');
        s.id = SENTINEL_ID;
        s.style.cssText = 'display:inline;font-size:0;width:0;overflow:hidden;';
        r.collapse(true);
        r.insertNode(s);
        return true;
      }
    }
    return false;
  };

  const saveRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (composeRef.current?.contains(r.commonAncestorContainer)) {
        savedRange.current = r.cloneRange();
      }
    }
  };

  const restoreRange = () => {
    const r = savedRange.current;
    if (!r) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(r);
  };

  const openVideoModal = (src, name) => {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.88);display:flex;flex-direction:column;align-items:center;justify-content:center;';
    const inner = document.createElement('div');
    inner.style.cssText = 'position:relative;max-width:90vw;max-height:90vh;display:flex;flex-direction:column;align-items:center;';
    inner.addEventListener('click', e => e.stopPropagation());
    const header = document.createElement('div');
    header.style.cssText = 'width:100%;display:flex;align-items:center;justify-content:space-between;padding:8px 12px;background:rgba(0,0,0,0.5);border-radius:8px 8px 0 0;gap:12px;box-sizing:border-box;';
    const title = document.createElement('span');
    title.textContent = name;
    title.style.cssText = 'font-size:13px;color:#fff;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;';
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕';
    closeBtn.style.cssText = 'background:none;border:none;color:#fff;cursor:pointer;font-size:16px;padding:4px;flex-shrink:0;';
    closeBtn.addEventListener('click', () => document.body.removeChild(overlay));
    header.appendChild(title);
    header.appendChild(closeBtn);
    const body = document.createElement('div');
    body.style.cssText = 'background:#111;border-radius:0 0 8px 8px;overflow:hidden;display:flex;align-items:center;justify-content:center;';
    const video = document.createElement('video');
    video.src = src;
    video.controls = true;
    video.autoplay = true;
    video.style.cssText = 'max-width:88vw;max-height:calc(90vh - 48px);display:block;';
    body.appendChild(video);
    inner.appendChild(header);
    inner.appendChild(body);
    overlay.appendChild(inner);
    overlay.addEventListener('click', () => { video.pause(); document.body.removeChild(overlay); });
    document.body.appendChild(overlay);
  };

  const buildFileNode = (file) => {
    const isImg = file.type?.startsWith('image/');
    const isVid = file.type?.startsWith('video/');
    const wrapper = document.createElement('span');
    wrapper.className = 'cmt-inline-img-wrap';
    wrapper.contentEditable = 'false';
    wrapper._file = file;
    if (isImg) {
      const img = document.createElement('img');
      img.src = URL.createObjectURL(file);
      img.className = 'cmt-inline-img-thumb';
      img.alt = file.name;
      wrapper.appendChild(img);
    } else if (isVid) {
      const src = URL.createObjectURL(file);
      const vid = document.createElement('video');
      vid.src = src + '#t=0.1';
      vid.className = 'cmt-inline-img-thumb cmt-inline-vid-thumb';
      vid.preload = 'metadata';
      vid.muted = true;
      vid.playsInline = true;
      vid.style.pointerEvents = 'none';
      wrapper.appendChild(vid);
      const play = document.createElement('span');
      play.className = 'cmt-inline-vid-play';
      play.textContent = '▶';
      play.style.pointerEvents = 'none';
      wrapper.appendChild(play);
      wrapper.style.cursor = 'pointer';
      wrapper.addEventListener('click', (e) => {
        if (e.target === wrapper || e.target === vid || e.target === play) openVideoModal(src, file.name);
      });
    } else {
      const chip = document.createElement('span');
      chip.className = 'cmt-inline-file-chip';
      chip.textContent = '📎 ' + file.name;
      wrapper.appendChild(chip);
    }
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'cmt-inline-img-remove';
    removeBtn.textContent = '×';
    removeBtn.addEventListener('click', () => { wrapper.remove(); setFileError(null); });
    wrapper.appendChild(removeBtn);
    return wrapper;
  };

  const insertFilesAtCaret = (files, useLiveSel = false) => {
    const el = composeRef.current;
    if (!el) return;

    // If a sentinel was planted (paperclip button path), insert before it
    const sentinel = el.querySelector(`#${SENTINEL_ID}`);
    if (sentinel) {
      files.forEach((file) => {
        const node = buildFileNode(file);
        sentinel.parentNode.insertBefore(node, sentinel);
      });
      sentinel.remove();
      updateHasContent();
      return;
    }

    // Paste path: use live selection
    if (useLiveSel) {
      const sel = window.getSelection();
      files.forEach((file) => {
        const node = buildFileNode(file);
        if (sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          range.insertNode(node);
          range.setStartAfter(node);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
          savedRange.current = range.cloneRange();
        } else {
          el.appendChild(node);
        }
      });
      updateHasContent();
      return;
    }

    // Fallback: restore saved range
    el.focus();
    restoreRange();
    const sel = window.getSelection();
    files.forEach((file) => {
      const node = buildFileNode(file);
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        range.insertNode(node);
        range.setStartAfter(node);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        savedRange.current = range.cloneRange();
      } else {
        el.appendChild(node);
      }
    });
    updateHasContent();
  };

  const extractComposeData = () => {
    const el = composeRef.current;
    if (!el) return { bodyWithMarkers: '', files: [] };
    const files = [];
    let body = '';
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        body += node.textContent;
      } else if (node.nodeName === 'BR') {
        body += '\n';
      } else if (node.nodeName === 'DIV') {
        body += '\n';
        node.childNodes.forEach(walk);
      } else if (node.id === SENTINEL_ID || node.id === EDIT_SENTINEL_ID) {
        // skip stale sentinel
      } else if (node.dataset?.mentionId) {
        body += `@[${node.dataset.mentionId}:${node.dataset.mentionName || node.textContent.slice(1)}]`;
      } else if (node._file instanceof File) {
        const idx = files.length;
        files.push(node._file);
        body += `[[new:${idx}]]`;
      } else {
        node.childNodes.forEach(walk);
      }
    };
    el.childNodes.forEach(walk);
    return { bodyWithMarkers: body.replace(/^\n|\n$/g, ''), files };
  };

  const clearCompose = () => {
    if (composeRef.current) composeRef.current.innerHTML = '';
    savedRange.current = null;
    setHasContent(false);
  };

  // ── Edit editor helpers ──────────────────────────────────────────────────

  const makeEditAttNode = ({ file, savedAtt }) => {
    const ct = file ? file.type : savedAtt?.content_type;
    const isImg = ct?.startsWith('image/');
    const isVid = ct?.startsWith('video/');
    const src = file ? URL.createObjectURL(file) : savedAtt?.file_url;
    const name = file ? file.name : (savedAtt?.file_name || 'file');
    const wrapper = document.createElement('span');
    wrapper.className = 'cmt-inline-img-wrap';
    wrapper.contentEditable = 'false';
    if (file) wrapper._file = file;
    if (savedAtt) wrapper._savedAtt = savedAtt;
    if (isImg) {
      const img = document.createElement('img');
      img.src = src;
      img.className = 'cmt-inline-img-thumb';
      img.alt = name;
      wrapper.appendChild(img);
    } else if (isVid) {
      const vid = document.createElement('video');
      vid.src = src + '#t=0.1';
      vid.className = 'cmt-inline-img-thumb cmt-inline-vid-thumb';
      vid.preload = 'metadata';
      vid.muted = true;
      vid.playsInline = true;
      vid.style.pointerEvents = 'none';
      wrapper.appendChild(vid);
      const play = document.createElement('span');
      play.className = 'cmt-inline-vid-play';
      play.textContent = '▶';
      play.style.pointerEvents = 'none';
      wrapper.appendChild(play);
      wrapper.style.cursor = 'pointer';
      wrapper.addEventListener('click', (e) => {
        if (e.target === wrapper || e.target === vid || e.target === play) openVideoModal(src, name);
      });
    } else {
      const chip = document.createElement('span');
      chip.className = 'cmt-inline-file-chip';
      chip.textContent = '📎 ' + name;
      wrapper.appendChild(chip);
    }
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'cmt-inline-img-remove';
    removeBtn.textContent = '×';
    removeBtn.addEventListener('click', () => { wrapper.remove(); setEditFileError(null); });
    wrapper.appendChild(removeBtn);
    return wrapper;
  };

  const editSaveRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (editRef.current?.contains(r.commonAncestorContainer)) editSavedRange.current = r.cloneRange();
    }
  };

  const editRestoreRange = () => {
    const r = editSavedRange.current;
    if (!r) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(r);
  };

  const EDIT_SENTINEL_ID = 'cmt-edit-caret-sentinel';

  const plantEditSentinel = (editorEl) => {
    editorEl.querySelector(`#${EDIT_SENTINEL_ID}`)?.remove();
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (editorEl.contains(r.commonAncestorContainer)) {
        const s = document.createElement('span');
        s.id = EDIT_SENTINEL_ID;
        s.style.cssText = 'display:inline;font-size:0;width:0;overflow:hidden;';
        r.collapse(true);
        r.insertNode(s);
        return true;
      }
    }
    return false;
  };

  const insertEditFilesAtCaret = (files, useLiveSel = false) => {
    const el = editRef.current;
    if (!el) return;

    const sentinel = el.querySelector(`#${EDIT_SENTINEL_ID}`);
    if (sentinel) {
      files.forEach((file) => {
        const node = makeEditAttNode({ file });
        sentinel.parentNode.insertBefore(node, sentinel);
      });
      sentinel.remove();
      return;
    }

    if (useLiveSel) {
      const sel = window.getSelection();
      files.forEach((file) => {
        const node = makeEditAttNode({ file });
        if (sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          range.insertNode(node);
          range.setStartAfter(node);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
          editSavedRange.current = range.cloneRange();
        } else {
          el.appendChild(node);
        }
      });
      return;
    }

    const scrollEl = el.closest('.comments-scroll, .cmt-edit-scroll, [data-cmt-scroll]') || el.parentElement;
    const savedScroll = scrollEl ? scrollEl.scrollTop : 0;
    el.focus({ preventScroll: true });
    editRestoreRange();
    const sel = window.getSelection();
    files.forEach((file) => {
      const node = makeEditAttNode({ file });
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        range.insertNode(node);
        range.setStartAfter(node);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        editSavedRange.current = range.cloneRange();
      } else {
        el.appendChild(node);
      }
    });
    if (scrollEl) scrollEl.scrollTop = savedScroll;
  };

  const extractEditData = () => {
    const el = editRef.current;
    if (!el) return { body: '', newFiles: [], keptAtts: [] };
    const newFiles = [];
    const keptAtts = [];
    const seenSavedAttIds = new Set();
    let newFileIdx = 0;
    let body = '';
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) { body += node.textContent; }
      else if (node.nodeName === 'BR') { body += '\n'; }
      else if (node.nodeName === 'DIV') { body += '\n'; node.childNodes.forEach(walk); }
      else if (node.dataset?.mentionId) {
        body += `@[${node.dataset.mentionId}:${node.dataset.mentionName || node.textContent.slice(1)}]`;
      } else if (node._file instanceof File) {
        body += `[[new:${newFileIdx}]]`;
        newFiles.push(node._file);
        newFileIdx++;
      } else if (node._savedAtt) {
        // Saved att — use stable id marker; no remap needed
        if (seenSavedAttIds.has(node._savedAtt.id)) return;
        seenSavedAttIds.add(node._savedAtt.id);
        keptAtts.push(node._savedAtt);
        body += `[[id:${node._savedAtt.id}]]`;
      } else {
        node.childNodes.forEach(walk);
      }
    };
    el.childNodes.forEach(walk);
    return { body: body.replace(/\n{3,}/g, '\n\n').trim(), newFiles, keptAtts };
  };

  // Paste HTML parts from splitPastedHtml: text goes in as plain text, and each
  // image gets a placeholder at its spot that is swapped for a real attachment
  // node once the image has been fetched.
  const pasteHtmlParts = (parts, { editorEl, makeNode, clamp, setErr, onChange }) => {
    const imgParts = parts.filter(p => p.type === 'img');
    const { allowed, allErrors } = clamp(imgParts, []);
    const allowedSet = new Set(allowed);
    const errors = [...allErrors];
    let pending = 0;
    const finish = () => {
      setErr(errors.length ? errors.join(' · ') : null);
      onChange?.();
    };
    parts.forEach((p) => {
      if (p.type === 'text') { document.execCommand('insertText', false, p.value); return; }
      if (!allowedSet.has(p)) return;
      const ph = document.createElement('span');
      ph.contentEditable = 'false';
      ph.title = `Loading ${p.name}…`;
      ph.style.cssText = 'display:inline-block;width:48px;height:48px;border-radius:6px;background:var(--bg-subtle, #eee);vertical-align:middle;margin:2px;';
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && editorEl.contains(sel.getRangeAt(0).commonAncestorContainer)) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        range.insertNode(ph);
        range.setStartAfter(ph);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      } else {
        editorEl.appendChild(ph);
      }
      pending++;
      fetchImageAsFile(p.src, p.name)
        .then((file) => {
          const { valid, errors: vErr } = validateFiles([file]);
          errors.push(...vErr);
          if (valid.length && ph.isConnected) ph.replaceWith(makeNode(valid[0]));
          else ph.remove();
        })
        .catch(() => { ph.remove(); errors.push(`Couldn't copy image "${p.name}" — attach it manually`); })
        .finally(() => { if (--pending === 0) finish(); });
    });
    if (pending === 0) finish();
  };

  const handleEditPaste = (e) => {
    e.preventDefault();
    const clipItems = Array.from(e.clipboardData?.items || []);
    const pastedFiles = clipItems.filter(i => i.kind === 'file').map(i => i.getAsFile()).filter(Boolean);
    if (pastedFiles.length) {
      const { valid, errors } = validateFiles(pastedFiles);
      const { allowed, allErrors } = clampToEditAttLimit(valid, errors);
      if (allErrors.length) setEditFileError(allErrors.join(' · '));
      else setEditFileError(null);
      if (allowed.length) {
        plantEditSentinel(editRef.current);
        insertEditFilesAtCaret(allowed);
      }
      return;
    }
    const htmlParts = splitPastedHtml(e.clipboardData.getData('text/html'));
    if (htmlParts) {
      pasteHtmlParts(htmlParts, {
        editorEl: editRef.current,
        makeNode: (file) => makeEditAttNode({ file }),
        clamp: clampToEditAttLimit,
        setErr: setEditFileError,
      });
      return;
    }
    const text = e.clipboardData.getData('text/plain');
    if (text) document.execCommand('insertText', false, text);
  };

  // Populate edit editor when editingId changes
  useEffect(() => {
    if (!editingId) return;
    const timer = setTimeout(() => {
      const el = editRef.current;
      if (!el) return;
      el.innerHTML = '';
      editSavedRange.current = null;
      const comment = itemsRef.current.find(c => c.id === editingId);
      if (!comment) return;
      const savedAtts = comment.attachments || [];
      const rawBody = comment.body || '';

      if (!savedAtts.length) {
        const cleaned = rawBody.replace(/\n?\[\[(?:att|id):\d+\]\]/g, '').trim();
        const html = cleaned.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          .replace(/@\[(\d+):([^\]]+)\]/g, (_, id, name) =>
            `<span class="mention-chip" contenteditable="false" data-mention-id="${id}" data-mention-name="${name}">@${name}</span>`
          );
        el.innerHTML = html;
        return;
      }

      const attById = {};
      savedAtts.forEach(a => { attById[String(a.id)] = a; });

      // Split on both [[id:X]] (new) and [[att:N]] (legacy index-based) markers
      const insertedAttIds = new Set();
      const parts = rawBody.split(/(\[\[(?:att|id):\d+\]\])/);
      parts.forEach((part) => {
        const idM  = part.match(/^\[\[id:(\d+)\]\]$/);
        const attM = part.match(/^\[\[att:(\d+)\]\]$/);
        if (idM) {
          const att = attById[idM[1]];
          if (att && !insertedAttIds.has(att.id)) {
            insertedAttIds.add(att.id);
            el.appendChild(makeEditAttNode({ savedAtt: att }));
          }
        } else if (attM) {
          // Legacy: index-based marker
          const att = savedAtts[Number(attM[1])];
          if (att && !insertedAttIds.has(att.id)) {
            insertedAttIds.add(att.id);
            el.appendChild(makeEditAttNode({ savedAtt: att }));
          }
        } else if (part) {
          const html = part.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/@\[(\d+):([^\]]+)\]/g, (_, id, name) =>
              `<span class="mention-chip" contenteditable="false" data-mention-id="${id}" data-mention-name="${name}">@${name}</span>`
            );
          const span = document.createElement('span');
          span.innerHTML = html;
          el.appendChild(span);
        }
      });
      // Append any attachments not referenced by a marker (old posts with no inline markers)
      savedAtts.forEach(a => {
        if (!insertedAttIds.has(a.id)) {
          insertedAttIds.add(a.id);
          el.appendChild(makeEditAttNode({ savedAtt: a }));
        }
      });

      // Place cursor at end
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      editSavedRange.current = range.cloneRange();
    }, 0);
    return () => clearTimeout(timer);
  }, [editingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const countEditAtts = () => editRef.current ? editRef.current.querySelectorAll('.cmt-inline-img-wrap').length : 0;
  const clampToEditAttLimit = (valid, errors) => {
    const slots = MAX_CMT_ATTACHMENTS - countEditAtts();
    if (slots <= 0) return { allowed: [], allErrors: [`Maximum ${MAX_CMT_ATTACHMENTS} attachments allowed`, ...errors] };
    const allowed = valid.slice(0, slots);
    const overflow = valid.length - allowed.length;
    return { allowed, allErrors: [...errors, ...(overflow > 0 ? [`${overflow} file(s) skipped — maximum ${MAX_CMT_ATTACHMENTS} attachments reached`] : [])] };
  };

  const handleEditFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const { valid, errors } = validateFiles(files);
    const { allowed, allErrors } = clampToEditAttLimit(valid, errors);
    if (allErrors.length) setEditFileError(allErrors.join(' · '));
    else setEditFileError(null);
    if (allowed.length) insertEditFilesAtCaret(allowed);
    e.target.value = '';
  };

  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const MAX_CMT_ATTACHMENTS = 100;
  const countComposeAtts = () => composeRef.current ? composeRef.current.querySelectorAll('.cmt-inline-img-wrap').length : 0;
  const clampToAttLimit = (valid, errors) => {
    const slots = MAX_CMT_ATTACHMENTS - countComposeAtts();
    if (slots <= 0) return { allowed: [], allErrors: [`Maximum ${MAX_CMT_ATTACHMENTS} attachments allowed`, ...errors] };
    const allowed = valid.slice(0, slots);
    const overflow = valid.length - allowed.length;
    return { allowed, allErrors: [...errors, ...(overflow > 0 ? [`${overflow} file(s) skipped — maximum ${MAX_CMT_ATTACHMENTS} attachments reached`] : [])] };
  };

  const handleComposeFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const { valid, errors } = validateFiles(files);
    const { allowed, allErrors } = clampToAttLimit(valid, errors);
    if (allErrors.length) setFileError(allErrors.join(' · '));
    else setFileError(null);
    if (allowed.length) insertFilesAtCaret(allowed);
    e.target.value = '';
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const clipItems = Array.from(e.clipboardData?.items || []);
    const pastedFiles = clipItems
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter(Boolean);
    if (pastedFiles.length) {
      const { valid, errors } = validateFiles(pastedFiles);
      const { allowed, allErrors } = clampToAttLimit(valid, errors);
      if (allErrors.length) setFileError(allErrors.join(' · '));
      else setFileError(null);
      if (allowed.length) {
        // Plant sentinel at paste position before any async work shifts focus
        plantSentinel(composeRef.current);
        insertFilesAtCaret(allowed);
      }
      return;
    }
    const htmlParts = splitPastedHtml(e.clipboardData.getData('text/html'));
    if (htmlParts) {
      pasteHtmlParts(htmlParts, {
        editorEl: composeRef.current,
        makeNode: buildFileNode,
        clamp: clampToAttLimit,
        setErr: setFileError,
        onChange: updateHasContent,
      });
      updateHasContent();
      return;
    }
    const text = e.clipboardData.getData('text/plain');
    if (text) document.execCommand('insertText', false, text);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.listComments(appId, taskId);
      if (mountedRef.current) setItems(r.items || []);
    } catch (err) {
      if (mountedRef.current) {
        const msg = err.response?.data?.error
          || (err.response ? `Server error ${err.response.status}` : 'Could not load comments — check connection');
        setError(msg);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [api, appId, taskId]);

  useEffect(() => { load(); }, [load]);

  const memberName = (id) => {
    if (id == null) return 'Someone';
    const u = members.find((m) => m.user_id === id);
    if (u) return memberDisplayName(u.first_name, u.last_name, u.email);
    if (me && me.id === id) {
      return memberDisplayName(me.firstName, me.lastName, me.email) || 'You';
    }
    return `User #${id}`;
  };

  const memberPhoto = (id) => {
    if (id == null) return null;
    const u = members.find((m) => m.user_id === id);
    return u?.photo_url || null;
  };

  const handlePost = async () => {
    const { bodyWithMarkers, files } = extractComposeData();
    if (!bodyWithMarkers && files.length === 0) return;
    setPosting(true);
    setError(null);
    try {
      let attachments = [];
      if (files.length > 0) attachments = await uploadFiles(files);
      // Send body with [[new:N]] markers; backend saves comment and returns attachments with DB ids.
      // Then PATCH the body replacing [[new:N]] with [[id:X]] using the returned ids.
      const saved = await api.addComment(appId, taskId, { body: bodyWithMarkers.trim(), attachments });
      const savedAtts = saved?.attachments || [];
      if (savedAtts.length > 0 && bodyWithMarkers.includes('[[new:')) {
        const finalBody = bodyWithMarkers.replace(/\[\[new:(\d+)\]\]/g, (_, n) => {
          const att = savedAtts[Number(n)];
          return att?.id != null ? `[[id:${att.id}]]` : '';
        });
        if (finalBody !== bodyWithMarkers) {
          try { await api.updateComment(appId, taskId, saved.id, { body: finalBody.trim() }); } catch (_) {}
        }
      }
      clearCompose();
      setFileError(null);
      await load();
    } catch (err) {
      const msg = err.response?.data?.error
        || err.message
        || (err.response ? `Server error ${err.response.status}` : 'Could not post comment — check connection and try again');
      setError(msg);
    } finally {
      setPosting(false);
    }
  };

  const handleSaveEdit = async (commentItem) => {
    const { body, newFiles, keptAtts } = extractEditData();
    if (!body && newFiles.length === 0 && keptAtts.length === 0) return;
    try {
      // Delete removed saved attachments
      const keptIds = new Set(keptAtts.map(a => a.id));
      const removedAtts = (commentItem.attachments || []).filter(a => !keptIds.has(a.id));
      const failedDeleteIds = new Set();
      for (const a of removedAtts) {
        try { await api.removeCommentAttachment(appId, taskId, commentItem.id, a.id); }
        catch (_) { failedDeleteIds.add(a.id); }
      }
      // Upload new files
      let uploadedAtts = [];
      if (newFiles.length > 0) uploadedAtts = await uploadFiles(newFiles);

      // Send body with [[new:N]] and [[id:X]] markers; backend saves new attachments and returns them with DB ids.
      // Then use the returned attachment ids to resolve [[new:N]] → [[id:X]] and PATCH body again.
      const savedEdit = await api.updateComment(appId, taskId, commentItem.id, { body: body.trim(), attachments: uploadedAtts });
      const allSavedAtts = savedEdit?.attachments || [];

      let finalBody = body;
      if (uploadedAtts.length > 0 && body.includes('[[new:')) {
        // Match [[new:N]] to the newly saved attachments (appended after kept ones)
        const newlySaved = allSavedAtts.filter(a => !keptAtts.find(k => k.id === a.id));
        finalBody = body.replace(/\[\[new:(\d+)\]\]/g, (_, n) => {
          const att = newlySaved[Number(n)];
          return att?.id != null ? `[[id:${att.id}]]` : '';
        });
      }

      // If any att deletion failed, ensure it still has an id marker in the body
      for (const a of (commentItem.attachments || []).filter(a => failedDeleteIds.has(a.id))) {
        if (!finalBody.includes(`[[id:${a.id}]]`)) finalBody += `\n[[id:${a.id}]]`;
      }

      if (finalBody !== body) {
        await api.updateComment(appId, taskId, commentItem.id, { body: finalBody.trim() || body.trim() });
      }
      setEditId(null);
      if (editRef.current) editRef.current.innerHTML = '';
      editSavedRange.current = null;
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await api.removeComment(appId, taskId, id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const composeBox = (placeholder, hint) => (
    <>
      <div style={{ position: 'relative' }}>
      <div
        className="cmt-compose-box cmt-compose-editable"
        ref={composeRef}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onBlur={saveRange}
        onMouseUp={() => { saveRange(); updateHasContent(); }}
        onKeyUp={() => { saveRange(); updateHasContent(); }}
        onInput={() => { updateHasContent(); composeMentionInput(); }}
        onKeyDown={e => {
          if (composeMentionKeyDown(e)) return;
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handlePost(); }
        }}
        onPaste={handlePaste}
      />
      <ComposeMentionDropdown />
      </div>
      <input ref={fileInputRef} type="file" multiple accept={ACCEPT_STRING} style={{ display: 'none' }} onChange={handleComposeFileChange} />
      {fileError && <div style={{ fontSize: 11, color: 'var(--status-danger-fg, #e53e3e)', marginTop: 4 }}>{fileError}</div>}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{hint}</span>
          <button
            onMouseDown={(e) => { e.preventDefault(); plantSentinel(composeRef.current); }}
            onClick={() => fileInputRef.current?.click()}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 2, display: 'flex' }}
            title="Attach file"
          >
            <Paperclip size={13} />
          </button>
        </div>
        <button
          className="btn-primary"
          style={{ fontSize: 11, padding: '4px 12px' }}
          onClick={handlePost}
          disabled={posting || uploading || !hasContent}
        >
          {posting || uploading ? 'Posting…' : 'Post'}
        </button>
      </div>
    </>
  );

  /* ─────────────────────────────────────────────────────────
     SIDEBAR (compact) mode
     ───────────────────────────────────────────────────────── */
  if (sidebarMode) {
    return (
      <div className="tdv2-panel">
        <div className="tdv2-panel-head">
          <span className="tdv2-panel-title">
            💬 Comments
            {items.length > 0 && (
              <span className="tdv2-panel-total" style={{ marginLeft: 5 }}>· {items.length}</span>
            )}
          </span>
        </div>

        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '4px 8px', marginBottom: 6, fontSize: 11 }}>
            <span style={{ color: '#dc2626', flex: 1 }}>{error}</span>
            <button onClick={() => { setError(null); load(); }}
              style={{ fontSize: 10, background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontWeight: 700, padding: 0 }}>
              Retry
            </button>
          </div>
        )}

        {loading ? (
          <p className="muted" style={{ fontSize: 11, margin: '4px 0' }}>Loading…</p>
        ) : items.length === 0 && !error ? (
          <p className="muted" style={{ fontSize: 11, margin: '4px 0' }}>No comments yet.</p>
        ) : (
          <ul style={{ margin: '0 0 8px', padding: 0, listStyle: 'none' }}>
            {items.slice(0, visibleCount).map((c) => {
              const isAuthor = me && c.author_user_id === me.id;
              const editing  = editingId === c.id;
              const name     = memberName(c.author_user_id);
              const photo    = memberPhoto(c.author_user_id);
              return (
                <li key={c.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <div className="tdv2-avatar sm" title={name}>
                    {photo
                      ? <img src={photo} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                      : initials(name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                      <span style={{ fontWeight: 700, fontSize: 11, color: 'var(--text-primary)' }}>{name}</span>
                      <span style={{ fontSize: 10, color: 'var(--text-secondary)', flex: 1, fontWeight: 500 }}
                        title={formatDateTime(c.created_at)}>
                        · {formatExactEntry(c.created_at)}
                      </span>
                      {c.is_query && <QueryBadge status={c.query_status} />}
                      {isAuthor && !editing && (
                        <>
                          <button className="btn-icon-only" title="Edit comment"
                            onClick={() => setEditId(c.id)}>✎</button>
                          <button className="btn-icon-only danger" title="Delete comment"
                            onClick={() => handleDelete(c.id)}>🗑</button>
                        </>
                      )}
                    </div>
                    {editing ? (
                      <>
                        <div style={{ border: '1px solid var(--border-focus)', borderRadius: 6, background: 'var(--bg-input)' }}>
                          <div style={{ position: 'relative' }}>
                          <div
                            ref={editRef}
                            className="cmt-compose-box cmt-compose-editable"
                            contentEditable
                            suppressContentEditableWarning
                            style={{ fontSize: 12, padding: '5px 7px', minHeight: 38, maxHeight: 200, overflowY: 'auto', outline: 'none' }}
                            onBlur={editSaveRange}
                            onMouseUp={editSaveRange}
                            onKeyUp={editSaveRange}
                            onInput={editMentionInput}
                            onKeyDown={e => { editMentionKeyDown(e); }}
                            onPaste={handleEditPaste}
                          />
                          <EditMentionDropdown />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 7px', borderTop: '1px solid var(--border)' }}>
                            <input ref={editFileInputRef} type="file" multiple accept={ACCEPT_STRING} style={{ display: 'none' }} onChange={handleEditFileChange} />
                            <button type="button" title="Attach file"
                              onMouseDown={(e) => { e.preventDefault(); plantEditSentinel(editRef.current); }}
                              onClick={() => editFileInputRef.current?.click()}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 2, display: 'flex' }}>
                              <Paperclip size={12} />
                            </button>
                          </div>
                        </div>
                        {editFileError && <div style={{ fontSize: 11, color: 'var(--status-danger-fg, #e53e3e)', marginTop: 3 }}>{editFileError}</div>}
                        <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end', marginTop: 4 }}>
                          <button className="btn-secondary" style={{ fontSize: 11, padding: '3px 9px' }}
                            onClick={() => { setEditId(null); setEditFileError(null); if (editRef.current) editRef.current.innerHTML = ''; }}>Cancel</button>
                          <button className="btn-primary" style={{ fontSize: 11, padding: '3px 9px' }}
                            onClick={() => handleSaveEdit(c)}>Save</button>
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.5, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                        {renderMixedContent(c.body, c.attachments, renderMentionText)}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {!loading && items.length > visibleCount && (
          <div style={{ marginBottom: 6, marginLeft: 30 }}>
            <button
              style={{ fontSize: 11, background: 'none', border: 'none', color: 'var(--brand)', cursor: 'pointer', padding: 0, fontWeight: 600 }}
              onClick={() => setVisibleCount(v => v + 15)}
            >
              View More
            </button>
          </div>
        )}
        {!loading && visibleCount > 10 && (
          <div style={{ marginBottom: 6, marginLeft: 30 }}>
            <button
              style={{ fontSize: 11, background: 'none', border: 'none', color: 'var(--brand)', cursor: 'pointer', padding: 0, fontWeight: 600 }}
              onClick={() => setVisibleCount(10)}
            >
              View Less
            </button>
          </div>
        )}

        <div className="tdv2-cmt-add">
          {composeBox('Write a comment… (type @ to mention)', '@ mention · Ctrl+Enter to post')}
        </div>
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────
     NORMAL (full-width) mode — original layout
     ───────────────────────────────────────────────────────── */
  return (
    <section className="task-section">
      <h2>Comments {items.length > 0 && <span className="muted small">· {items.length}</span>}</h2>

      {error && <div className="banner banner-error">{error}</div>}

      {loading ? (
        <p className="muted">Loading comments…</p>
      ) : items.length === 0 ? (
        <p className="muted">No comments yet. Start the conversation below.</p>
      ) : (
        <ul className="comments-list">
          {items.map((c) => {
            const isAuthor = me && c.author_user_id === me.id;
            const editing  = editingId === c.id;
            return (
              <li key={c.id} className="comment-row">
                <div className="comment-head">
                  <strong>{memberName(c.author_user_id)}</strong>
                  <span className="comment-timestamp" title={formatDateTime(c.created_at)}>
                    {formatExactEntry(c.created_at)}
                  </span>
                  {c.is_query && <QueryBadge status={c.query_status} />}
                </div>
                {editing ? (
                  <>
                    <div style={{ border: '1px solid var(--border-focus)', borderRadius: 7, background: 'var(--bg-input)' }}>
                      <div style={{ position: 'relative' }}>
                      <div
                        ref={editRef}
                        className="cmt-compose-box cmt-compose-editable"
                        contentEditable
                        suppressContentEditableWarning
                        style={{ padding: '7px 10px', fontSize: 13, minHeight: 38, maxHeight: 240, overflowY: 'auto', outline: 'none' }}
                        onBlur={editSaveRange}
                        onMouseUp={editSaveRange}
                        onKeyUp={editSaveRange}
                        onInput={editMentionInput}
                        onKeyDown={e => { editMentionKeyDown(e); }}
                        onPaste={handleEditPaste}
                      />
                      <EditMentionDropdown />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderTop: '1px solid var(--border)' }}>
                        <input ref={editFileInputRef} type="file" multiple accept={ACCEPT_STRING} style={{ display: 'none' }} onChange={handleEditFileChange} />
                        <button type="button" title="Attach file"
                          onMouseDown={(e) => { e.preventDefault(); editSaveRange(); }}
                          onClick={() => editFileInputRef.current?.click()}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 2, display: 'flex' }}>
                          <Paperclip size={13} />
                        </button>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Paste or attach images/files</span>
                      </div>
                    </div>
                    {editFileError && <div style={{ fontSize: 11, color: 'var(--status-danger-fg, #e53e3e)', marginTop: 3 }}>{editFileError}</div>}
                    <div className="form-actions" style={{ marginTop: 6 }}>
                      <button className="btn-link-sm" onClick={() => { setEditId(null); setEditFileError(null); if (editRef.current) editRef.current.innerHTML = ''; }}>Cancel</button>
                      <button className="btn-primary small" onClick={() => handleSaveEdit(c)}>Save</button>
                    </div>
                  </>
                ) : (
                  <div className="comment-body">
                    {renderMixedContent(c.body, c.attachments, renderMentionText)}
                  </div>
                )}
                {!editing && (
                  <div className="comment-actions">
                    {isAuthor && (
                      <button className="btn-link-sm small" onClick={() => setEditId(c.id)}>Edit</button>
                    )}
                    {isAuthor && (
                      <button className="btn-link-sm danger small" onClick={() => handleDelete(c.id)}>Delete</button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="comment-add">
        {composeBox('Write a comment… (type @ to mention someone)', 'Type @ to mention a team member · Ctrl+Enter to post')}
      </div>
    </section>
  );
}
